import api from '@/libs/api'
import store from '@/store'

const getProjectId = () => store.state.app.projectId
const environment = 'snitch'

const currentTimeISO = new Date().toISOString()
const oneHourAgo = new Date(new Date() - 60 * 60 * 1000).toISOString()

export default {
  namespaced: true,
  state: {
    fetchingHealthcheckLogs: false,
    gettingHealthcheckLog: false,
    healthcheckLogsResultInfo: {},
    currentPage: 1,
    healthcheckLogs: [],
    healthcheckLogsCharts: [],
    healthcheckLog: {},
    selectedDataCenter: { id: 0, name: 'All', slug: 'Data Centers' },
    selectedAggregation: { id: 0, name: 'Response Time Average' },
    selectedInterval: { id: 0, value: 'Every Minute' },
    healthcheckDataCenters: [],
    healthcheckLogsCount: null,
    fetchingHealthcheckLogsCount: false,
    fetchingHealthcheckStats: false,
    healthcheckStats: [],
    customDate: [],
    dateRange: {
      dateFrom: oneHourAgo,
      dateTo: currentTimeISO,
      healthcheckId: 0,
    },
  },
  getters: {
    currentPage: state => state.currentPage,
  },
  mutations: {
    SET_FETCHING_HEALTHCHECK_LOGS(state, value) {
      state.fetchingHealthcheckLogs = value
    },
    CLEAR_FILTERED_HEALTHCHECK_LOGS(state) {
      state.healthcheckLogsCharts = []
    },
    CLEAR_HEALTHCHECK_LOGS(state) {
      state.healthcheckLogs = []
      state.healthcheckStats = []
    },
    SET_HEALTHCHECK_LOGS(state, value) {
      state.healthcheckLogs = value.result
      state.healthcheckLogsResultInfo = value.resultInfo
    },
    SET_HEALTHCHECK_LOGS_RESULTS(state, value) {
      state.healthcheckLogs = value.result.result
      state.healthcheckLogsResultInfo = {
        nextCount: value.result.nextCount,
        previousCount: value.result.previousCount,
        totalCount: value.result.totalCount,
      }
    },
    SET_FILTERED_HEALTHCHECK_LOGS(state, value) {
      state.healthcheckLogsCharts = value.result
    },
    SET_HEALTHCHECK_LOG(state, value) {
      state.healthcheckLog = value
    },
    SET_GETTING_HEALTHCHECK_LOG(state, value) {
      state.gettingHealthcheckLog = value
    },
    SET_CURRENT_PAGE(state, value) {
      state.currentPage = value
    },
    SET_SELECTED_DATACENTER(state, value) {
      state.selectedDataCenter = value
    },
    SET_SELECTED_AGGREGATION(state, value) {
      state.selectedAggregation = value
    },
    SET_HEALTHCHECK_DATACENTERS(state, value) {
      state.healthcheckDataCenters = value
    },
    SET_SELECTED_INTERVAL(state, value) {
      state.selectedInterval = value
    },
    SET_FETCHING_HEALTHCHECK_LOGS_COUNT(state, value) {
      state.fetchingHealthcheckLogsCount = value
    },
    SET_HEALTHCHECK_LOGS_COUNT(state, value) {
      state.healthcheckLogsCount = value
    },
    SET_GETTING_STATS(state, value) {
      state.fetchingHealthcheckStats = value
    },
    SET_HEALTHCHECK_STATS(state, value) {
      state.healthcheckStats = value
    },
    SET_CUSTOM_DATE(state, value) {
      state.customDate = value
    },
    DESTROY_CUSTOM_DATE(state) {
      state.customDate = []
    },
    SET_DATE_RANGE(state, value) {
      state.dateRange = value
    },
  },
  actions: {
    fetchHealthcheckLogs({ commit }, { healthcheckId, query }) {
      commit('SET_FETCHING_HEALTHCHECK_LOGS', true)
      const filteringQuery = `?PageIndex=${query.pageIndex}&PageSize=${query.pageSize}&State=${query.state}&DataCenterId=${query.dataCenterId || 0}`
      return new Promise((resolve, reject) => {
        api(environment).get(`projects/${getProjectId()}/healthchecks/${healthcheckId}/logs${filteringQuery}`)
          .then(response => {
            commit('SET_HEALTHCHECK_LOGS', response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_FETCHING_HEALTHCHECK_LOGS', false) })
      })
    },
    fetchHealthcheckLogsResults({ commit }, { healthcheckId, query }) {
      commit('SET_FETCHING_HEALTHCHECK_LOGS', true)
      return new Promise((resolve, reject) => {
        api(environment).post(`projects/${getProjectId()}/healthchecks/${healthcheckId}/logs/results`, query)
          .then(response => {
            commit('SET_HEALTHCHECK_LOGS_RESULTS', response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_FETCHING_HEALTHCHECK_LOGS', false) })
      })
    },
    fetchFilteredHealthcheckLogs({ commit }, { healthcheckId, query }) {
      commit('SET_FETCHING_HEALTHCHECK_LOGS', true)
      const filteringQuery = `?State=${query.state}&DataCenterId=${query.dataCenterId || 0}&Interval=${query.interval}`
      return new Promise((resolve, reject) => {
        api(environment).get(`projects/${getProjectId()}/healthchecks/${healthcheckId}/logs/analytics${filteringQuery}`)
          .then(response => {
            commit('SET_FILTERED_HEALTHCHECK_LOGS', response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_FETCHING_HEALTHCHECK_LOGS', false) })
      })
    },
    fetchHealthcheckLogsCount({ commit }, { healthcheckId }) {
      commit('SET_FETCHING_HEALTHCHECK_LOGS_COUNT', true)
      return new Promise((resolve, reject) => {
        api(environment).get(`projects/${getProjectId()}/healthchecks/${healthcheckId}/logs/count`)
          .then(response => {
            commit('SET_HEALTHCHECK_LOGS_COUNT', response.data.result)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_FETCHING_HEALTHCHECK_LOGS_COUNT', false) })
      })
    },
    getHealthcheckLog({ commit }, { healthcheckId, healthcheckLogId }) {
      commit('SET_GETTING_HEALTHCHECK_LOG', true)
      return new Promise((resolve, reject) => {
        api(environment).get(`projects/${getProjectId()}/healthchecks/${healthcheckId}/logs/${healthcheckLogId}`)
          .then(response => {
            commit('SET_HEALTHCHECK_LOG', response.data.result)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_GETTING_HEALTHCHECK_LOG', false) })
      })
    },
    fetchHealthcheckStats({ commit }, { healthcheckId, query }) {
      commit('SET_GETTING_STATS', true)
      return new Promise((resolve, reject) => {
        api(environment).post(`projects/${getProjectId()}/healthchecks/${healthcheckId}/logs/stats`, query)
          .then(response => {
            commit('SET_HEALTHCHECK_STATS', response.data.result)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_GETTING_STATS', false) })
      })
    },
  },
}
